import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {ISLANDS, propertyUrl } from "@/lib/translations";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DateRangeField } from "@/components/DateRangeField";

const LAST_KEY = "islasol_last_search";

export default function SearchWidget() {
  const { t } = useLang();
  const navigate = useNavigate();
  const saved = (() => { try { return JSON.parse(localStorage.getItem(LAST_KEY)) || {}; } catch { return {}; } })();
  const today = new Date().toISOString().slice(0, 10);
  const datesValid = saved.checkin && saved.checkin >= today;
  const [island, setIsland] = useState(saved.island || "all");
  const [checkin, setCheckin] = useState(datesValid ? saved.checkin : "");
  const [checkout, setCheckout] = useState(datesValid ? saved.checkout || "" : "");
  const [guests, setGuests] = useState(saved.guests || 2);
  const [ref, setRef] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const n = ref.trim().replace(/\D/g, "");
    if (n) {
      const r = await api.get("/properties", { params: { ref: n } });
      if (r.data.length === 1) return navigate(propertyUrl(r.data[0]));
      if (r.data.length > 1) return navigate(`/properties?ref=${n}`);
      toast.error(t.filters.refNotFound || t.property.refLabel);
      return;
    }
    localStorage.setItem(LAST_KEY, JSON.stringify({ island, checkin, checkout, guests }));
    const p = new URLSearchParams();
    if (island !== "all") p.set("island", island);
    if (guests) p.set("guests", guests);
    if (checkin) p.set("checkin", checkin);
    if (checkout) p.set("checkout", checkout);
    navigate(`/properties?${p.toString()}`);
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white/92 backdrop-blur-xl rounded-2xl p-5 md:p-6 shadow-2xl grid grid-cols-2 md:grid-cols-6 gap-3 items-end"
      data-testid="hero-search"
    >
      <div className="col-span-2 md:col-span-1">
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-white mb-1.5">
          {t.hero.searchIsland}
        </label>
        <Select value={island} onValueChange={setIsland}>
          <SelectTrigger data-testid="hero-search-island-select" className="bg-white" aria-label={t.hero.searchIsland}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">{t.hero.allIslands}</SelectItem>
            {ISLANDS.map((i) => (
              <SelectItem key={i} value={i}>{t.islands[i]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DateRangeField checkin={checkin} checkout={checkout} onChange={({ checkin: ci, checkout: co }) => { setCheckin(ci); setCheckout(co); }} />

      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-white mb-1.5">{t.hero.guests}</label>
        <Input type="number" min={1} max={16} value={guests} onChange={(e) => setGuests(+e.target.value)} aria-label={t.hero.guests}
               data-testid="hero-search-guests-input" className="bg-white" />
      </div>
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-white mb-1.5">{t.property.refLabel}</label>
        <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="z. B. 1234" inputMode="numeric"
               data-testid="hero-search-ref-input" className="bg-white" />
      </div>
      <Button type="submit" className="bg-brand hover:bg-brand-hover text-white h-10 col-span-2 md:col-span-1"
              data-testid="hero-search-submit-btn">
        <Search className="w-4 h-4 mr-2" /> {t.hero.search}
      </Button>
    </form>
  );
}
